import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ScrollView,
  Pressable,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, ClothingItem, Club } from '../types';
import {
  AppHeader,
  EmptyState,
  ItemCard,
  PageBand,
  ScreenLayout,
  SearchInput,
  Select,
} from '../components';
import { useResponsive } from '../utils/responsive';
import { LAYOUT } from '../constants/layout';
import { Brand, Colors, Fonts, Radii, Spacing, Typography } from '../constants';
import { itemsService, ItemSortOrder } from '../services/items';
import { clubService } from '../services/club';
import { optionListsService } from '../services/optionLists';
import { analyticsService } from '../services/analytics';
import { useDialog, useUser } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'Browse'>;

/** Quick-filter value meaning "no category filter". */
const ALL_CATEGORIES = 'all';

/** Used until `option_lists` answers (and when it has no category rows). */
const FALLBACK_CATEGORIES = ['jersey', 'shorts', 'socks', 'jacket', 'other'];

interface ChipOption {
  label: string;
  value: string;
}

export const BrowseScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { search, club_id } = route.params || {};
  const { user } = useUser();

  // All active clubs power the filter so it works for guests too (not just the
  // logged-in user's own clubs). Public SELECT on `clubs` makes this safe.
  const [allClubs, setAllClubs] = useState<Club[]>([]);
  const [categories, setCategories] = useState<ChipOption[]>([]);

  const [items, setItems] = useState<ClothingItem[]>([]);
  // Total matching rows on the server — `items` only holds the pages loaded so far.
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedClubId, setSelectedClubId] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_CATEGORIES);
  const [sortOrder, setSortOrder] = useState<ItemSortOrder>('newest');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { isDesktop } = useResponsive();
  const PAGE_SIZE = 20;

  // The filter combination a request belongs to. A response is only applied
  // while its signature is still the active one, so an in-flight loadMore for
  // the previous filter can't append items from another club/category — and,
  // unlike a plain counter, a fresh replace is never invalidated by an append
  // that started after it.
  const filterSignatureRef = useRef('');
  // Mirrors `loadingMore` synchronously: state updates are async, so two
  // onEndReached calls in the same frame would otherwise both start a page.
  const loadingMoreRef = useRef(false);

  const loadItems = async (
    clubId: string,
    searchFilter: string,
    categoryFilter: string,
    order: ItemSortOrder,
    append = false,
    currentItemsLength = 0
  ) => {
    const signature = `${clubId}|${searchFilter}|${categoryFilter}|${order}`;
    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      filterSignatureRef.current = signature;
      setLoading(true);
    }
    try {
      const currentOffset = append ? currentItemsLength : 0;
      const filters: Parameters<typeof itemsService.getItems>[0] = {
        status: 'available',
        orderBy: order,
        limit: PAGE_SIZE,
        offset: currentOffset,
      };
      if (clubId && clubId !== 'all') {
        filters.clubId = clubId;
      }
      if (categoryFilter && categoryFilter !== ALL_CATEGORIES) {
        filters.category = categoryFilter;
      }
      // Server-side search filtering
      if (searchFilter && searchFilter.trim()) {
        filters.search = searchFilter;
      }
      const result = await itemsService.getItems(filters);

      // Discard the response if the filters changed while it was in flight.
      if (signature !== filterSignatureRef.current) return;

      if (append) {
        setItems(prev => [...prev, ...result.data]);
      } else {
        setItems(result.data);
        setTotalCount(result.total);
        // Track search analytics only for new searches (not pagination) with an active search term
        if (searchFilter && searchFilter.trim()) {
          analyticsService.trackSearch(searchFilter, result.data.length);
        }
      }

      // Check if there are more items to load
      // Note: If total items is an exact multiple of PAGE_SIZE, this causes one extra
      // API call that returns empty. This is acceptable behavior for simplicity.
      setHasMore(result.data.length === PAGE_SIZE);
    } catch (error: any) {
      console.error('[BrowseScreen] Failed to load items:', error);
      dialog.alert(t('common.errors.title'), t('screens.browse.loadError'), 'error');
    } finally {
      // Only clear the flags this request owns: a superseded replace leaves
      // `loading` to the newer one that is still running.
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else if (signature === filterSignatureRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Apply route params whenever navigation updates them. React Navigation
  // REPLACES params on navigate (no merge), so an absent param must reset the
  // corresponding filter — otherwise a previously selected club/search sticks
  // around after e.g. the navbar's `navigate('Browse', {})`.
  useEffect(() => {
    setSearchTerm(search ?? '');
    setSelectedClubId(club_id ? (Array.isArray(club_id) ? club_id[0] : club_id) : 'all');
  }, [search, club_id]);

  // Load all clubs once for the filter dropdown (works for guests too).
  useEffect(() => {
    clubService
      .getClubs()
      .then(setAllClubs)
      .catch(error => console.error('[BrowseScreen] Failed to load clubs:', error));
  }, []);

  // Category quick filters come from the shared option list (same source as the
  // listing form); the enum copy is the fallback when it is empty/unavailable.
  useEffect(() => {
    optionListsService
      .getOptions('category')
      .then(options => setCategories(options.map(o => ({ label: o.label, value: o.value }))))
      .catch(error => console.error('[BrowseScreen] Failed to load categories:', error));
  }, []);

  // Load items when filters change (also handles initial load)
  useEffect(() => {
    setHasMore(true); // Reset pagination when filters change
    loadItems(selectedClubId, searchTerm, selectedCategory, sortOrder);
  }, [selectedClubId, searchTerm, selectedCategory, sortOrder]);

  // Load more items (infinite scroll)
  const loadMore = useCallback(() => {
    if (!loadingMoreRef.current && !loadingMore && hasMore && !loading) {
      loadItems(selectedClubId, searchTerm, selectedCategory, sortOrder, true, items.length);
    }
  }, [
    loadingMore,
    hasMore,
    loading,
    selectedClubId,
    searchTerm,
    selectedCategory,
    sortOrder,
    items.length,
  ]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setHasMore(true); // Reset pagination on refresh
    loadItems(selectedClubId, searchTerm, selectedCategory, sortOrder);
  }, [selectedClubId, searchTerm, selectedCategory, sortOrder]);

  // Prepare club filter options from all clubs (sorted alphabetically).
  const clubOptions = useMemo(() => {
    const sortedClubs = [...allClubs].sort((a, b) =>
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    );
    return [
      { label: t('screens.browse.allClubs'), value: 'all' },
      ...sortedClubs.map(club => ({ label: club.name || 'Unknown Club', value: club.id })),
    ];
  }, [allClubs, t]);

  const sortOptions = useMemo(
    () => [
      { label: t('screens.browse.sortNewest'), value: 'newest' },
      { label: t('screens.browse.sortPriceAsc'), value: 'price_asc' },
    ],
    [t]
  );

  const categoryChips = useMemo<ChipOption[]>(() => {
    const options = categories.length
      ? categories
      : FALLBACK_CATEGORIES.map(value => ({
          label: t(`enums.category.${value}`, { defaultValue: value }),
          value,
        }));
    return [{ label: t('screens.browse.categoryAll'), value: ALL_CATEGORIES }, ...options];
  }, [categories, t]);

  const openItem = (item: ClothingItem) => navigation.navigate('ItemDetails', { itemId: item.id });

  const renderFooter = () => {
    if (!loadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={Colors.primary} />
      </View>
    );
  };

  // "128 items in VV Zwolle" — the overline kicker on the band and, without the
  // club, the overline above the grid. Counts every match, not just the pages
  // loaded so far. Uppercase comes from `Typography.overline`.
  const selectedClubName =
    selectedClubId !== 'all' ? allClubs.find(club => club.id === selectedClubId)?.name : undefined;
  const resultCount = t('screens.browse.itemCount', { count: totalCount });
  const bandKicker = selectedClubName
    ? `${resultCount} ${t('screens.browse.inClub', { club: selectedClubName })}`
    : resultCount;

  const renderChip = (option: ChipOption) => {
    const selected = selectedCategory === option.value;
    // "All" clears the filter, so tapping it again is a no-op — no ✕ affordance.
    const clearable = selected && option.value !== ALL_CATEGORIES;
    return (
      <Pressable
        key={option.value}
        onPress={() =>
          setSelectedCategory(selected ? ALL_CATEGORIES : option.value)
        }
        style={[styles.chip, selected && styles.chipSelected]}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={option.label}
        hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
        testID={`browse-category-${option.value}`}
      >
        <Text style={[styles.chipText, selected && styles.chipTextSelected]} numberOfLines={1}>
          {option.label}
        </Text>
        {clearable ? (
          <Ionicons name="close" size={14} color={Colors.white} style={styles.chipIcon} />
        ) : null}
      </Pressable>
    );
  };

  // Search · club · sort. On desktop this is the white panel straddling the
  // band's lower edge; it lives outside the list so the input keeps focus.
  const toolbar = (
    <>
      <SearchInput
        bordered
        value={searchTerm}
        onChangeText={setSearchTerm}
        placeholder={t('screens.browse.searchPlaceholder')}
        testID="browse-search"
      />
      {clubOptions.length > 1 && (
        <Select
          value={selectedClubId}
          options={clubOptions}
          onValueChange={setSelectedClubId}
          placeholder={t('screens.browse.allClubs')}
          style={[styles.toolbarSelect, styles.clubSelect]}
        />
      )}
      <Select
        value={sortOrder}
        options={sortOptions}
        onValueChange={value => setSortOrder(value as ItemSortOrder)}
        style={[styles.toolbarSelect, styles.sortSelect]}
      />
    </>
  );

  // Only the first load takes over the screen; a filter change or a refresh
  // keeps the current results (and the RefreshControl) on screen until the
  // new page arrives.
  const showLoadingState = loading && items.length === 0;

  const renderGrid = () => {
    if (showLoadingState) {
      return (
        <View style={styles.centerContainer}>
          <Text>{t('common.loading')}</Text>
        </View>
      );
    }

    if (items.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <EmptyState
            variant="full"
            icon="search-outline"
            title={t('screens.browse.noResults')}
            subtitle={t('screens.browse.noResultsSubtext')}
          />
        </View>
      );
    }

    return (
      <FlatList
        key={isDesktop ? 'desktop-grid' : 'mobile-grid'}
        data={items}
        keyExtractor={item => item.id}
        numColumns={isDesktop ? LAYOUT.grid.desktop.columns : 2}
        columnWrapperStyle={isDesktop ? styles.gridRowDesktop : styles.gridRowMobile}
        renderItem={({ item }) => (
          <ItemCard
            item={item}
            square={!isDesktop}
            onPress={() => openItem(item)}
            style={isDesktop ? styles.gridCellDesktop : styles.gridCellMobile}
          />
        )}
        contentContainerStyle={isDesktop ? styles.listContentDesktop : styles.listContentMobile}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={5}
        removeClippedSubviews={!isDesktop}
        onEndReached={loadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
      />
    );
  };

  return (
    <ScreenLayout
      mobileTab="Browse"
      // Guests get no FAB: they can't list anything until they've signed in
      onFabPress={user ? () => navigation.navigate('ItemForm') : undefined}
      band={
        isDesktop ? (
          <PageBand title={t('screens.browse.title')} kicker={bandKicker} toolbar={toolbar} />
        ) : undefined
      }
    >
      <View style={styles.container}>
        {/* Mobile header + its own search row; the band carries these on desktop */}
        {!isDesktop && (
          <>
            <AppHeader
              navigation={navigation}
              currentScreen="Browse"
              title={t('screens.browse.title')}
            />
            <View style={styles.mobileSearchRow}>
              <SearchInput
                bordered
                value={searchTerm}
                onChangeText={setSearchTerm}
                placeholder={t('screens.browse.searchPlaceholder')}
                testID="browse-search"
              />
            </View>
          </>
        )}

        {isDesktop ? (
          <View style={styles.chipsRowDesktop}>{categoryChips.map(renderChip)}</View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipsRowMobile}
            style={styles.chipsScroll}
          >
            {categoryChips.map(renderChip)}
          </ScrollView>
        )}

        {isDesktop && !showLoadingState && items.length > 0 ? (
          <Text style={styles.resultsOverline}>{resultCount}</Text>
        ) : null}

        {renderGrid()}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Spacing.xl,
  },
  footerLoader: {
    paddingVertical: Spacing.lg,
    alignItems: 'center',
  },

  // ── Toolbar (desktop band) ──────────────────────────────────────────────
  toolbarSelect: {
    marginBottom: 0,
    flexShrink: 0,
  },
  clubSelect: {
    width: 240,
  },
  sortSelect: {
    width: 200,
  },

  // ── Mobile search row ───────────────────────────────────────────────────
  mobileSearchRow: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.md,
  },

  // ── Quick-filter chips ──────────────────────────────────────────────────
  chipsScroll: {
    // A horizontal ScrollView has no intrinsic height inside a column; pin it
    // to the chip height plus its vertical padding so it never collapses.
    flexGrow: 0,
    flexShrink: 0,
    height: 32 + Spacing.md * 2,
  },
  // Desktop: the page padding sits on these rows and on the list content, not
  // on the column, so the scrollbar runs along the column's edge instead of
  // through the gutter.
  chipsRowDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
    marginBottom: Spacing.xl,
    paddingHorizontal: Spacing['3xl'],
  },
  chipsRowMobile: {
    flexDirection: 'row',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: Spacing.md,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    backgroundColor: Colors.background,
  },
  chipSelected: {
    backgroundColor: Brand.bosgroen,
    borderColor: Brand.bosgroen,
  },
  chipText: {
    fontFamily: Fonts.medium,
    fontSize: 13,
    fontWeight: '500',
    color: Colors.textMedium,
  },
  chipTextSelected: {
    color: Colors.white,
  },
  chipIcon: {
    marginLeft: 6,
  },

  // ── Results ─────────────────────────────────────────────────────────────
  resultsOverline: {
    ...Typography.overline,
    color: Colors.textLight,
    marginBottom: Spacing.base,
    paddingHorizontal: Spacing['3xl'],
  },
  // Cells take a fixed share of the row (gutters via half-padding on the cell
  // and a negative margin on the row) so a lone card in the last row keeps the
  // same width as the others instead of stretching across the whole row.
  gridCellDesktop: {
    width: `${100 / LAYOUT.grid.desktop.columns}%`,
    paddingHorizontal: LAYOUT.grid.desktop.gap / 2,
  },
  gridCellMobile: {
    width: '50%',
    paddingHorizontal: Spacing.md / 2,
  },
  gridRowDesktop: {
    marginHorizontal: -(LAYOUT.grid.desktop.gap / 2),
    marginBottom: 28,
  },
  gridRowMobile: {
    marginHorizontal: -(Spacing.md / 2),
    marginBottom: Spacing.md,
  },
  listContentDesktop: {
    paddingHorizontal: Spacing['3xl'],
    paddingBottom: Spacing['4xl'],
  },
  listContentMobile: {
    paddingHorizontal: Spacing.base,
    paddingBottom: LAYOUT.fab.contentPadding, // Browse has the floating "+"
  },
});
